import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { getPatient, deletePatient, Patient } from "../api/patients";
import { useAuth } from "../auth/AuthContext";
import PatientCard from "../components/PatientCard";
import ScanHistoryList from "../components/ScanHistoryList";
import NeurologistAssessment from "../components/NeurologistAssessment";
import PatientNotes from "../components/PatientNotes";
import PatientDiseasesSection from "../components/PatientDiseasesSection";

export default function PatientDetailPage() {
  const { patientId } = useParams<{ patientId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [patient, setPatient] = useState<Patient | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const isAdmin = user?.role === "admin" || user?.is_superuser;
  const isAssistant = user?.role === "assistant";

  useEffect(() => {
    if (!patientId) return;
    setLoading(true);
    getPatient(patientId)
      .then(setPatient)
      .catch(() => setError("Пациент не найден"))
      .finally(() => setLoading(false));
  }, [patientId]);

  const handleDelete = async () => {
    if (!patient?.patient_id) return;
    setDeleting(true);
    try {
      await deletePatient(patient.patient_id);
      setConfirmOpen(false);
      navigate("/admin");
    } catch {
      setError("Ошибка при удалении");
      setDeleting(false);
      setConfirmOpen(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !patient) {
    return <Alert severity="error">{error || "Пациент не найден"}</Alert>;
  }

  return (
    <Box>
      <PatientCard patient={patient} />

      <Divider sx={{ my: 3 }} />

      <Typography variant="h6" sx={{ mb: 2 }}>
        История сканов
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Нажмите на «Единый тест», чтобы открыть результаты (аудио, транскрипции, MST)
      </Typography>
      <ScanHistoryList patientId={patient.patient_id!} />

      <Divider sx={{ my: 3 }} />

      <PatientDiseasesSection
        patient={patient}
        onPatientUpdated={setPatient}
        readOnly={isAssistant}
      />

      {!isAssistant ? (
        <>
          <Divider sx={{ my: 3 }} />

          <NeurologistAssessment
            patient={patient}
            onPatientUpdated={setPatient}
          />
        </>
      ) : (
        <>
          <Divider sx={{ my: 3 }} />

          <PatientNotes
            patient={patient}
            onPatientUpdated={setPatient}
          />
        </>
      )}

      {isAdmin && (
        <>
          <Divider sx={{ my: 3 }} />
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setConfirmOpen(true)}
          >
            Удалить пациента
          </Button>

          <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
            <DialogTitle>Удалить пациента?</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Пациент <strong>{patient.patient_id}</strong> будет удалён.
                Связанные сканы останутся, но будут отвязаны от пациента.
                Это действие нельзя отменить.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirmOpen(false)} disabled={deleting}>
                Отмена
              </Button>
              <Button onClick={handleDelete} color="error" variant="contained" disabled={deleting}>
                {deleting ? "Удаление..." : "Удалить"}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  );
}
